<template>
  <div id="hubSearchSelector" :class="componentClasses">
    <OnClickOutside @trigger="blurSelector">
      <div class="selector-container" ref="selectContainer" :class="containerClasses">
        <input
          type="text"
          autocomplete="new-password"
          :value="inputValue"
          :readonly="importable"
          @focus="expandList"
          @input="search"
        />

        <div class="drop-down">
          <inline-svg :src="$assets.svg['selection_arrow']" width="100%" height="100%" fill="#808285" />
        </div>

        <label for="select-label">{{ label }}</label>

        <div
          class="list-box"
          v-if="hasListData && focused"
          ref="selectOptionList"
          :style="{
            top: listTop,
            bottom: listBottom,
            left: listLeft,
            right: listRight,
            minWidth: listMinWidth,
            height: listMaxHeight,
          }"
        >
          <ul>
            <li
              class="option hub-link"
              v-for="(data, index) in filteredDataList"
              :key="index"
              @click="selectOption(data)"
              @keypress.enter="selectOption(data)"
            >
              {{ data.name || data || '' }}
            </li>
          </ul>
        </div>
      </div>
    </OnClickOutside>
  </div>
</template>

<script>
import { OnClickOutside } from '@vueuse/components';

export default {
  name: 'FormSearchSelector',
  props: {
    label: {
      type: String,
      default: '',
    },
    center: Boolean,
    gradual: Boolean,
    importable: Boolean,

    modelValue: {
      type: Number,
      default: null,
    },
    valid: Boolean,
    dataList: {
      type: Array,
      default: () => {
        return [];
      },
    },
  },

  data() {
    return {
      focused: false,
      expanded: false,
      selectedOption: {
        id: null,
        name: '',
      },
      inputValue: '',

      // Option List Styles
      listTop: '',
      listBottom: '',
      listLeft: '',
      listRight: '',
      listMinWidth: '',
      listMaxWidth: '',
      listMaxHeight: '',
    };
  },

  methods: {
    expandList() {
      // Reset search keyworkd
      this.inputValue = '';
      this.focused = true;

      // No data
      if (!this.hasListData) {
        return;
      }

      this.expanded = true;

      this.placeList();
    },

    async placeList() {
      // Reset all dynamic styles
      this.listTop = '';
      this.listBottom = '';
      this.listLeft = '';
      this.listRight = '';
      this.listMinWidth = '';
      this.listMaxWidth = '';
      this.listMaxHeight = '';

      await this.$nextTick();

      /**
       * Start to calculate the dimensions of option list
       */
      const containerElement = this.$refs.selectContainer;
      const docElement = containerElement.ownerDocument.documentElement;

      // Vertical
      const screenHeight = docElement.clientHeight;
      const containerBottom = containerElement.getBoundingClientRect().bottom;
      const bottomDistance = screenHeight - containerBottom;
      const topDistance = containerElement.getBoundingClientRect().top;

      // Horizontal
      const screenWidth = docElement.clientWidth;
      const leftDistance = containerElement.getBoundingClientRect().left;

      // Vertical calculation
      const listMaxHeight = this.$refs.selectOptionList.getBoundingClientRect().height;

      if (bottomDistance > 0.4 * screenHeight) {
        if (bottomDistance < listMaxHeight) {
          this.listMaxHeight = `calc(${bottomDistance}px - 0.5em)`;
        }

        this.listTop = 'calc(100%)';
      } else {
        if (topDistance < listMaxHeight) {
          this.listMaxHeight = `calc(${topDistance}px - 0.5em)`;
        }

        this.listBottom = 'calc(100%)';
      }

      // Horizontal calculation
      const listClientWidth = this.$refs.selectOptionList.getBoundingClientRect().width;

      this.listMinWidth = 'calc(100% + 1em)';

      if (leftDistance + listClientWidth > screenWidth) {
        this.listRight = '-0.5em';
      } else {
        this.listLeft = '-0.5em';
      }
    },

    blurSelector() {
      if (this.focused) {
        this.focused = false;
        this.reloadValue();
      }
    },

    search(e) {
      this.inputValue = e.target.value;
    },

    selectOption(data) {
      this.selectedOption = {
        id: data.id || data || null,
        name: data.name || data || '',
      };

      this.blurSelector();
    },

    reloadValue() {
      this.inputValue = this.selectedOption.name;
      this.$emit('update:modelValue', this.selectedOption.id);
    },
  },

  computed: {
    filteredDataList() {
      return this.dataList.filter((optionData) =>
        RegExp(this.inputValue, 'i').test(optionData.name || optionData || '')
      );
    },

    hasListData() {
      return this.filteredDataList.length > 0;
    },

    componentClasses() {
      return {
        focused: this.focused,
        expanded: this.expanded,
      };
    },

    containerClasses() {
      return {
        center: this.center,
        gradual: this.gradual,
        valid: this.valid,
        filled: this.selectedOption.name,
      };
    },
  },

  watch: {
    dataList: {
      handler(list) {
        const validOption = list.find((el) => `${el.name || el || ''}`.trim() === this.selectedOption.name);

        this.selectedOption = {
          id: validOption ? validOption.id : null,
          name: validOption ? validOption.name : '',
        };

        this.reloadValue();
      },
    },

    modelValue(val) {
      const validOption = this.dataList.find((el) => (el.id || el || null) === val);

      this.selectedOption = {
        id: validOption ? validOption.id : null,
        name: validOption ? validOption.name : '',
      };

      this.reloadValue();
    },
  },

  components: {
    OnClickOutside,
  },
};
</script>

<style scoped>
#hubSearchSelector {
  position: relative;
  box-sizing: border-box;
  padding: 1.8em 0 0.2em;
  border-bottom: 1px solid #bbbdbf;
}
#hubSearchSelector.expanded {
  z-index: 1;
}
#hubSearchSelector.expanded.focused {
  z-index: 2;
}

.selector-container {
  position: relative;
  cursor: pointer;
}

/** Selector drop down button */
.selector-container .drop-down {
  position: absolute;
  bottom: 0;
  right: 0.6em;
  width: 1.2em;
  height: 1.2em;
  pointer-events: none;
}

/** Selector input style */
.selector-container input {
  flex-grow: 1;
  box-sizing: border-box;
  text-overflow: ellipsis;
  outline: none;
  /* width: calc(100% - 2em); */
  width: 100%;
  border: 0 none;
  padding-right: 2em;
  background: transparent;
  color: #6d6e70;
  font-size: 1.1em;
  cursor: pointer;
  transition: color 0.2s ease;
}
.selector-container.center input {
  text-align: center;
}
#hubSearchSelector:not(.focused) .selector-container.valid input {
  color: #76be45;
}
#hubSearchSelector:not(.focused) .selector-container:not(.valid).filled input {
  color: #df195f;
}

/** Selector label style */
.selector-container label {
  position: absolute;
  left: 0;
  bottom: 0;
  color: #6d6e70;
  font-size: 1.1em;
  pointer-events: none;
}
.selector-container.center label {
  left: 50%;
  transform: translateX(-50%);
}
#hubSearchSelector.focused .selector-container label,
.selector-container.filled label {
  left: 0;
  bottom: 3em;
  transform: translateX(0);
  font-size: 0.65em;
}
.selector-container.gradual label {
  transition: all 0.2s;
}

/* Option list */
.list-box {
  position: absolute;
  box-sizing: border-box;
  border: 1px solid #a7a9ac;
  border-radius: 0.4em;
  box-shadow: 0 0 0.8em 0 #a7a9ac;
  padding: 0.5em 0;
  background: #fff;
  overflow: hidden;
}

.list-box ul {
  width: 100%;
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
}

.list-box .option {
  padding: 0.8em 1em;
  background: #fff;
  text-align: left;
  transition: background-color 0.3s, color 0.3s;
}
.list-box .option:not(:last-child) {
  border-bottom: 1px solid #a7a9ac;
}
.list-box .option:hover {
  background: #8ac640;
  color: #fff;
}

/* overwrite browser settings to make sure scroll bar is visible when it's needed */
/* only chrome-derivative browsers (webkit) supported for now */
::-webkit-scrollbar {
  -webkit-appearance: none;
  width: 0.5em;
  background: #fff;
}
::-webkit-scrollbar-thumb {
  border-radius: 0.5em;
  background: rgba(0, 0, 0, 0.5);
  -webkit-box-shadow: 0 0 1px rgba(255, 255, 255, 0.5);
  box-shadow: 0 0 1px rgba(255, 255, 255, 0.5);
}
</style>
